import React, { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { Avatar, ButtonBase, Typography } from '@mui/material';
import { FileText, Mail } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import type { EmailThread } from './email-thread';
import { formatEntryTime, getEmailEntrySummary } from './email-thread';
const useStyles = makeStyles()(theme => ({
  attachmentCount: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: 4,
  },
  card: {
    '&.Mui-focusVisible': {
      borderColor: theme.appColors.primary,
    },
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackgroundSubtle,
    },
    alignItems: 'flex-start',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 8,
    boxShadow: '0px 0px 3.4px rgba(0,0,0,0.05)',
    gap: 12,
    justifyContent: 'flex-start',
    padding: theme.spacing(1.75, 1.5, 1.5),
    textAlign: 'left',
    width: '100%',
  },
  contents: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  heading: {
    alignItems: 'baseline',
    display: 'flex',
    gap: theme.spacing(1),
  },
  iconSlot: {
    backgroundColor: theme.appColors.selectedBackgroundNav,
    flexShrink: 0,
    height: 32,
    width: 32,
  },
  meta: {
    color: theme.palette.text.secondary,
    lineHeight: '16px',
  },
  preview: {
    flex: 1,
    minWidth: 0,
  },
  previewRow: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    gap: theme.spacing(1),
  },
  time: {
    flexShrink: 0,
  },
  title: {
    flex: 1,
    fontWeight: 600,
    lineHeight: '16px',
  },
}));

interface ActivityEmailEntryCardProps {
  onOpen: (activityItemId: UUID) => void;
  thread: EmailThread;
}

/** One email thread in the activity list. Opens the thread in the panel. */
export const ActivityEmailEntryCard = React.memo(function ActivityEmailEntryCard({
  onOpen,
  thread,
}: ActivityEmailEntryCardProps) {
  const { classes, cx, theme } = useStyles();
  const summary = getEmailEntrySummary(thread);
  const { activityItemId } = thread;
  const open = useCallback(() => onOpen(activityItemId), [activityItemId, onOpen]);

  return (
    <ButtonBase className={classes.card} onClick={open}>
      <Avatar aria-hidden className={classes.iconSlot}>
        <Mail color={theme.appColors.primary} size={18} />
      </Avatar>
      <span className={classes.contents}>
        <span className={classes.heading}>
          <Typography noWrap className={classes.title} component="span" variant="caption">
            {summary.title}
          </Typography>
          <Typography className={cx(classes.meta, classes.time)} component="span" variant="caption">
            {formatEntryTime(summary.createdAt)}
          </Typography>
        </span>
        <Typography noWrap className={classes.meta} component="span" variant="caption">
          {summary.context}
        </Typography>
        <span className={classes.previewRow}>
          <Typography
            noWrap
            className={cx(classes.meta, classes.preview)}
            component="span"
            variant="caption"
          >
            {summary.preview}
          </Typography>
          {summary.attachmentCount > 0 && (
            <Typography
              aria-label={`${summary.attachmentCount} attachments`}
              className={cx(classes.meta, classes.attachmentCount)}
              component="span"
              variant="caption"
            >
              <FileText size={12} />
              {summary.attachmentCount}
            </Typography>
          )}
        </span>
      </span>
    </ButtonBase>
  );
});
